import { faCirclePlus } from "@fortawesome/pro-solid-svg-icons"
import {
  Box,
  Button,
  Container,
  FormControlLabel,
  Grid,
  Switch,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material"
import { hasRole } from "auth/hasRole"
import { useApiDelete } from "auth/useApi2"
import { useApi } from "auth/useApi3"
import { ActionIconButtonTypeEnum } from "components/Actions/ActionIconButton/actionIcon.interface"
import ActionButtonIcon from "components/Actions/ActionIconButton/ActionIconButton"
import { AppContext } from "components/AppContextProvider"
import ClpTable from "components/ClpTable"
import { ClpTableEnum } from "components/ClpTable/ClpTableEnum"
import {
  DefaultColumnFilter,
  MultiSelectFilter,
  ServerSideMultiSelectFilter
} from "components/ClpTable/ClpTableFilterTypes"
import TableButton from "components/ClpTable/components/actions/TableButton/TableButton"
import { StyledTableButtonGroup } from "components/ClpTable/components/actions/TableButton/tableButtonGroup.styled"
import { TableButtonTypeEnum } from "components/ClpTable/components/actions/TableButton/tableButtonProps.interface"
import TableDropdown, {
  StatusDropdown
} from "components/ClpTable/components/actions/TableDropdown/TableDropdown"
import TableLink from "components/ClpTable/components/actions/TableLink/TableLink"
import { includesPrimitiveFilter } from "components/ClpTable/components/helpers/includesFilterFn"
import { StyledBoldTypography } from "components/ClpTable/components/typography/typography.styled"
import ClpTableServerSidePaging from "components/ClpTable/ServerSidePaging"
import { showDateTime } from "components/DateHandler/dateHandler"
import ContinueDialog from "components/Dialogs/ContinueDialog"
import SuccessDialog from "components/Dialogs/SuccessDialog"
import { GlobalLoaderContext } from "components/GlobalLoadingProvider"
import ChipInfoIcon from "components/Status/ChipInfo/ChipInfoIcon"
import ChipPriority from "components/Status/ChipInfo/ChipPriority"
import { ViewToggler } from "components/ViewToggler/ViewToggler"
import { apiEndpoints } from "generated/apiEndpoints"
import { allRoles } from "generated/roles"
import { allRoutes } from "generated/routes"
import ImportIssueReportForm from "pages/issuereports/components/CustomForms/ImportIssueReportForm"
import { useContext, useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { Link, useHistory } from "react-router-dom"
import { StyledTableContainer } from "shared/styled/Global"
import { ActionType } from "shared/vehicle/components/InspectionBookingSidebar.interfaces"
import { initialModalState, ModalDialog } from "shared/vehicle/components/ModalDialog"
import { issueReportChip } from "../../issurereport.interface"
import { issueReportStatusData, prio } from "./components/table/helpers"

const Archive = () => {
  const { t } = useTranslation()
  const appContext = useContext(AppContext)
  const loadingContext = useContext(GlobalLoaderContext)
  const history = useHistory()
  const theme = useTheme()
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [clickedRow, setClickedRow] = useState()
  const [showContinueModal, setShowContinueModal] = useState(false)
  const [deleteResult, deleteIssueReport] = useApiDelete()
  const [groupVehicles, setGroupVehicles] = useState(false)
  const [issueReportsResults, issueReportsApi] = useApi([])
  const [issueReportsHistoryResults, issueReportsHistoryApi] = useApi([])
  const [issueReportsGroupedResults, issueReportsGroupedApi] = useApi([])
  const [historyData, setHistoryData] = useState([])
  const [issueReportsData, setIssueReportsData] = useState([])
  const [issueReportsGroupData, setIssueReportsGroupData] = useState([])
  const [modalData, setModalData] = useState(initialModalState)
  const [updatedIssueReportAttribute, setUpdatedIssueReportAttribute] = useState({})
  const [refreshKey, setRefreshKey] = useState(0)
  const [issueReportPutResult, issueReportPutApi] = useApi()
  const [issueReportFilterOptionsResult, issueReportFilterOptionsApi] = useApi()
  const [issueReportFilterOptions, setIssueReportFilterOptions] = useState({
    departmentOptions: [],
    categoryOptions: []
  })
  const priorityOptions = [0, 1, 2, 3, 4].map((priority) => ({ value: priority, name: prio(priority, t) }))
  const statusOptions = [1, 2, 3, 4, 5].map((status) => ({
    value: status,
    name: issueReportStatusData(status, theme, t).label
  }))
  const userHasIssueReportAdmin = hasRole(appContext.appUser, allRoles.IssueReportsAdmin)

  const viewList = [
    { value: "issueReports", label: t("issuereports.issuereports", "Issue reports") },
    { value: "history", label: t("global.history", "History") }
  ]
  const [view, setView] = useState(viewList[0].value)

  useEffect(() => {
    issueReportsApi.get(apiEndpoints.issuereports.getissuereportsarchive)
    loadingContext.setLoading(() => issueReportsResults.status === 1)
    loadingContext.setLoading(() => issueReportsGroupedResults.status === 1)
    issueReportsGroupedApi.get(apiEndpoints.issuereports.getissuereportsgroupedbyvehiclesandbycustomerid)

    return () => {
      issueReportsApi.leave()
      issueReportsGroupedApi.leave()
    }
  }, [issueReportsApi])

  useEffect(() => {
    issueReportsHistoryApi.get(apiEndpoints.issuereports.getissuereportsarchivehistory)
    loadingContext.setLoading(() => issueReportsHistoryResults.status === 1)

    return () => {
      issueReportsApi.leave()
    }
  }, [issueReportsApi])

  useEffect(() => {
    if (issueReportsResults.status === 2) {
      setIssueReportsData(issueReportsResults.data)
    }
  }, [issueReportsResults])

  useEffect(() => {
    if (issueReportsHistoryResults.status === 2) {
      setHistoryData(issueReportsHistoryResults.data)
    }
  }, [issueReportsHistoryResults])

  useEffect(() => {
    if (issueReportsGroupedResults.status !== 2) return
    setIssueReportsGroupData(issueReportsGroupedResults.data)
  }, [issueReportsGroupedResults])

  useEffect(() => {
    if (issueReportPutResult.status !== 2) return
    setIssueReportsData((prevState) => {
      return prevState
        .filter((issueReport) => issueReport.status !== 5)
        .map((issueReport) => {
          if (issueReport.id === updatedIssueReportAttribute.id) {
            return { ...issueReport, ...updatedIssueReportAttribute }
          }
          return issueReport
        })
    })
    setHistoryData((prevState) => {
      return prevState
        .filter((issueReport) => issueReport.status === 5)
        .map((issueReport) => {
          if (issueReport.id === updatedIssueReportAttribute.id) {
            return { ...issueReport, ...updatedIssueReportAttribute }
          }
          return issueReport
        })
    })
    setIssueReportsGroupData((prevState) => {
      return prevState.map((vehicle) => {
        return {
          ...vehicle,
          subRows: vehicle.subRows.map((issueReport) => {
            if (issueReport.id === updatedIssueReportAttribute.id) {
              return { ...issueReport, ...updatedIssueReportAttribute }
            }
            return issueReport
          })
        }
      })
    })
    issueReportsHistoryApi.get(apiEndpoints.issuereports.getissuereportsarchivehistory)
    setRefreshKey((x) => x + 1)
  }, [issueReportPutResult.status])

  useEffect(() => {
    if (deleteResult.status !== 2) return
    issueReportsApi.get(apiEndpoints.issuereportsadmin.getissuereportsarchive)
    if (hasRole(appContext.appUser, allRoles.IssueReportsAdmin)) {
      issueReportsGroupedApi.get(apiEndpoints.issuereports.getissuereportsgroupedbyvehiclesandbycustomerid)
    }
  }, [deleteResult])

  useEffect(() => {
    if (issueReportFilterOptionsResult.status !== 2) return
    setIssueReportFilterOptions(issueReportFilterOptionsResult.data)
  }, [issueReportFilterOptionsResult])

  useEffect(() => {
    issueReportFilterOptionsApi.get(apiEndpoints.issuereports.getissuereportfilteroptions)
  }, [])

  const updateExistingData = () => {
    setRefreshKey((x) => x + 1)
    issueReportsApi.get(apiEndpoints.issuereportsadmin.getissuereportsarchive)
    if (hasRole(appContext.appUser, allRoles.IssueReportsAdmin)) {
      issueReportsGroupedApi.get(apiEndpoints.issuereports.getissuereportsgroupedbyvehiclesandbycustomerid)
    }
  }

  const handleDelete = () => {
    deleteIssueReport(apiEndpoints.issuereportsadmin.deleteissuereport.replace("{id}", clickedRow))
    setShowContinueModal(!showContinueModal)
    setShowSuccessModal(!showSuccessModal)
  }

  const openContinueModal = (cell) => {
    setClickedRow(cell)
    setShowContinueModal(!showContinueModal)
  }

  const groupedColumns = [
    {
      accessorKey: "regNumber",
      header: t("global.table.regnr", "Regnr"),
      cell: ({ cell }) => {
        return (
          <TableLink
            label={cell.row.original.regNumber}
            to={allRoutes.VehicleregistryVehiclesDetails(cell.row.original.vehicleId)}
          />
        )
      }
    },
    {
      accessorKey: "internNumber",
      header: t("global.table.internal_number", "Internal no")
    },
    {
      accessorKey: "vehicleId",
      header: "",
      meta: {
        align: "right"
      },
      cell: ({ cell }) => {
        return (
          hasRole(appContext.appUser, allRoles.IssueReportsAdmin) && (
            <ActionButtonIcon
              type={ActionIconButtonTypeEnum.Custom}
              custom={{
                icon: faCirclePlus,
                label: t("global.create_work_order", "Create work order"),
                color: "secondary"
              }}
              handleOnClick={() => {
                history.push({
                  pathname: allRoutes.IssuereportsWorkordersAdd(),
                  state: cell.row.original.vehicleId
                })
              }}
            />
          )
        )
      }
    }
  ]

  const columns = [
    {
      accessorKey: "id",
      header: t("global.table.id", "ID"),
      cell: ({ cell }) => {
        return <StyledBoldTypography>{cell.row.original.id}</StyledBoldTypography>
      },
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "vehicleRegNumber",
      header: t("global.table.regnr", "Reg No"),
      meta: { filter: DefaultColumnFilter },
      cell: ({ cell }) => {
        return (
          <TableLink
            label={cell.row.original.vehicleRegNumber}
            to={allRoutes.VehicleregistryVehiclesDetails(cell.row.original.vehicleId)}
          />
        )
      }
    },
    {
      accessorKey: "vehicleInternNumber",
      header: t("global.table.internal_number", "Internal No"),
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "issueReportCategoryName",
      header: t("global.table.category", "Category"),
      filterFn: "arrIncludesSome",
      meta: {
        filter: ServerSideMultiSelectFilter,
        mainFilter: true,
        filterHeader: t("global.table.category", "Category"),
        filterOptions: issueReportFilterOptions.categoryOptions.map((c) => ({
          name: c,
          value: c
        }))
      }
    },
    {
      accessorKey: "vehicleDepartmentName",
      header: t("global.table.section", "Section"),
      filterFn: "arrIncludesSome",
      meta: {
        mainFilter: true,
        filter: ServerSideMultiSelectFilter,
        filterOptions: issueReportFilterOptions.departmentOptions.map((d) => ({
          name: d,
          value: d
        }))
      }
    },
    {
      accessorKey: "meterSetting",
      header: t("global.table.meter_setting", "Meter setting"),
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "description",
      header: t("global.table.description", "Description"),
      meta: { filter: DefaultColumnFilter }
    },
    {
      id: "workOrder",
      accessorFn: (row) => row.workOrderId && `#${row.workOrderId}`,
      header: t("global.table.workorder", "Work order"),
      meta: { filter: DefaultColumnFilter }
    },
    {
      id: "priority",
      accessorFn: (row) => row.priority,
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      cell: ({ cell }) => {
        return userHasIssueReportAdmin ? (
          <TableDropdown
            initialValue={cell.row.original.priority}
            handleOnChange={(event) => {
              const newVal = Number(event.target.value)
              setModalData({
                actionType: ActionType.Confirm,
                isOpen: true,
                title: t("global.change_priority", "Change priority"),
                message: `${t("global.change_the_priority_of")} ${cell.row.original?.issueReportCategory?.name
                  } ${t("global.from")} ${prio(cell.row?.original?.priority, t)} ${t("global.to")} ${prio(
                    newVal,
                    t
                  )}?`
              })
              setUpdatedIssueReportAttribute({ id: cell.row.original.id, priority: newVal })
            }}
            options={priorityOptions}
          />
        ) : (
          <ChipPriority type={cell.row.original.priority} />
        )
      },
      header: t("global.table.priority", "Priority"),
      meta: {
        filterOptions: [
          { value: 0, name: t("global.none", "None") },
          { value: 1, name: t("global.low", "Low") },
          { value: 2, name: t("global.medium", "Medium") },
          { value: 3, name: t("global.high", "High") },
          { value: 4, name: t("vehicleregistry.driving_ban", "Driving Ban") }
        ],
        mainFilter: true,
        filter: ServerSideMultiSelectFilter
      }
    },
    {
      id: "status",
      accessorFn: (row) => row.status,
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      cell: ({ cell }) => {
        const currentStatusData = issueReportStatusData(cell.row?.original?.status, theme, t)
        return userHasIssueReportAdmin ? (
          <StatusDropdown
            onChange={(event) => {
              const newStatusData = issueReportStatusData(event.target.value, theme, t)
              setModalData({
                actionType: ActionType.Confirm,
                isOpen: true,
                title: t("global.change_status", "Change status"),
                message: `${t(
                  "global.change_the_status_of",
                  "Are you sure you want to change the status of"
                )} ${cell.row.original?.issueReportCategory?.name}
                ${t("global.from", "from")} ${currentStatusData.modalLabel} ${t("global.to", "to")} ${newStatusData.modalLabel
                  }?`
              })
              setUpdatedIssueReportAttribute({ id: cell.row.original.id, status: event.target.value })
            }}
            value={cell.row?.original?.status || 1}
            options={statusOptions}
            label={currentStatusData.label}
            icon={currentStatusData.icon}
            color={currentStatusData.color}
            backgroundColor={currentStatusData.backgroundColor}
            iconColor={currentStatusData.iconColor}
          />
        ) : (
          <ChipInfoIcon type={issueReportChip(cell.row?.original?.status)} />
        )
      },
      header: t("global.table.status", "Status"),
      meta: {
        filter: ServerSideMultiSelectFilter,
        filterHeader: t("global.table.status", "Status"),
        align: useMediaQuery(theme.breakpoints.up("xl")) ? "left" : "right",
        mainFilter: true,
        filterOptions:
          view === "issueReports"
            ? [
              {
                name: t("learning.titles.not_started", "Not Started"),
                value: 0
              },
              {
                name: t("global.new", "New"),
                value: 1
              },
              {
                name: t("global.planned", "Planned"),
                value: 2
              },
              {
                name: t("global.ongoing", "Ongoing"),
                value: 3
              },
              {
                name: t("global.fixed", "Fixed"),
                value: 4
              }
            ]
            : [{ name: t("global.completed", "Completed"), value: 5 }]
      }
    },
    {
      accessorKey: "createdDate",
      header: t("global.created", "Created"),
      cell: ({ cell }) => <Box>{showDateTime(cell.row.original?.createdDate)}</Box>,
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "updatedDate",
      header: t("global.table.last_updated", "Last updated"),
      cell: ({ cell }) => <Box>{showDateTime(cell.row.original?.updatedDate)}</Box>,
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "actions",
      header: "",
      meta: {
        hiddenFilter: true,
        align: "right"
      },
      cell: ({ cell }) => {
        return (
          <StyledTableButtonGroup>
            {hasRole(appContext.appUser, allRoles.IssueReportsAdmin) && (
              <>
                <TableButton
                  type={TableButtonTypeEnum.Edit}
                  handleOnClick={() => {
                    history.push(allRoutes.IssuereportsArchiveIssuereport(cell.row.original.id))
                  }}
                />
                {/* {!appContext.appUser.customer.issueReportDeleteDisabled && (
                  <TableButton
                    type={TableButtonTypeEnum.Delete}
                    handleOnClick={() => {
                      openContinueModal(cell.row.original.id)
                    }}
                  />
                )} */}
              </>
            )}
          </StyledTableButtonGroup>
        )
      }
    }
  ]
  const historyColumns = [
    {
      accessorKey: "id",
      header: t("global.table.id", "ID"),
      cell: ({ cell }) => {
        return <StyledBoldTypography>{cell.row.original.id}</StyledBoldTypography>
      },
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "vehicle.regNumber",
      header: t("global.table.regnr", "Reg No"),
      meta: { filter: DefaultColumnFilter },
      cell: ({ cell }) => {
        return (
          <TableLink
            label={cell.row.original.vehicle.regNumber}
            to={allRoutes.VehicleregistryVehiclesDetails(cell.row.original.vehicle.id)}
          />
        )
      }
    },
    {
      accessorKey: "vehicle.internnumber",
      header: t("global.table.internal_number", "Internal No"),
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "issueReportCategory.name",
      header: t("global.table.category", "Category"),
      filterFn: "arrIncludesSome",
      meta: {
        filter: ServerSideMultiSelectFilter,
        mainFilter: true,
        filterHeader: t("global.table.category", "Category"),
        filterOptions: issueReportFilterOptions.categoryOptions.map((c) => ({
          name: c,
          value: c
        }))
      }
    },
    {
      accessorKey: "vehicle.department.name",
      header: t("global.table.section", "Section"),
      filterFn: "arrIncludesSome",
      meta: {
        mainFilter: true,
        filter: ServerSideMultiSelectFilter,
        filterOptions: issueReportFilterOptions.departmentOptions.map((d) => ({
          name: d,
          value: d
        }))
      }
    },
    {
      accessorKey: "meterSetting",
      header: t("global.table.meter_setting", "Meter setting"),
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "description",
      header: t("global.table.description", "Description"),
      meta: { filter: DefaultColumnFilter }
    },
    {
      id: "workOrder",
      accessorFn: (row) => row.workOrderId && `#${row.workOrderId}`,
      header: t("global.table.workorder", "Work order"),
      meta: { filter: DefaultColumnFilter }
    },
    {
      id: "priority",
      accessorFn: (row) => row.priority,
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      cell: ({ cell }) => {
        return userHasIssueReportAdmin ? (
          <TableDropdown
            initialValue={cell.row.original.priority}
            handleOnChange={(event) => {
              const newVal = Number(event.target.value)
              setModalData({
                actionType: ActionType.Confirm,
                isOpen: true,
                title: t("global.change_priority", "Change priority"),
                message: `${t("global.change_the_priority_of")} ${cell.row.original?.issueReportCategory?.name
                  } ${t("global.from")} ${prio(cell.row?.original?.priority, t)} ${t("global.to")} ${prio(
                    newVal,
                    t
                  )}?`
              })
              setUpdatedIssueReportAttribute({ id: cell.row.original.id, priority: newVal })
            }}
            options={priorityOptions}
          />
        ) : (
          <ChipPriority type={cell.row.original.priority} />
        )
      },
      header: t("global.table.priority", "Priority"),
      meta: {
        filterOptions: [
          { value: 0, name: t("global.none", "None") },
          { value: 1, name: t("global.low", "Low") },
          { value: 2, name: t("global.medium", "Medium") },
          { value: 3, name: t("global.high", "High") },
          { value: 4, name: t("vehicleregistry.driving_ban", "Driving Ban") }
        ],
        mainFilter: true,
        filter: ServerSideMultiSelectFilter
      }
    },
    {
      id: "status",
      accessorFn: (row) => row.status,
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      cell: ({ cell }) => {
        const currentStatusData = issueReportStatusData(cell.row?.original?.status, theme, t)
        return userHasIssueReportAdmin ? (
          <StatusDropdown
            onChange={(event) => {
              const newStatusData = issueReportStatusData(event.target.value, theme, t)
              setModalData({
                actionType: ActionType.Confirm,
                isOpen: true,
                title: t("global.change_status", "Change status"),
                message: `${t(
                  "global.change_the_status_of",
                  "Are you sure you want to change the status of"
                )} ${cell.row.original?.issueReportCategory?.name}
              ${t("global.from", "from")} ${currentStatusData.modalLabel} ${t("global.to", "to")} ${newStatusData.modalLabel
                  }?`
              })
              setUpdatedIssueReportAttribute({ id: cell.row.original.id, status: event.target.value })
            }}
            value={cell.row?.original?.status || 1}
            options={statusOptions}
            label={currentStatusData.label}
            icon={currentStatusData.icon}
            color={currentStatusData.color}
            backgroundColor={currentStatusData.backgroundColor}
            iconColor={currentStatusData.iconColor}
          />
        ) : (
          <ChipInfoIcon type={issueReportChip(cell.row?.original?.status)} />
        )
      },
      header: t("global.table.status", "Status"),
      meta: {
        filter: ServerSideMultiSelectFilter,
        filterHeader: t("global.table.status", "Status"),
        align: useMediaQuery(theme.breakpoints.up("xl")) ? "left" : "right",
        mainFilter: true,
        filterOptions:
          view === "issueReports"
            ? [
              {
                name: t("learning.titles.not_started", "Not Started"),
                value: 0
              },
              {
                name: t("global.new", "New"),
                value: 1
              },
              {
                name: t("global.planned", "Planned"),
                value: 2
              },
              {
                name: t("global.ongoing", "Ongoing"),
                value: 3
              },
              {
                name: t("global.fixed", "Fixed"),
                value: 4
              }
            ]
            : [{ name: t("global.completed", "Completed"), value: 5 }]
      }
    },
    {
      accessorKey: "createdDate",
      header: t("global.created", "Created"),
      cell: ({ cell }) => <Box>{showDateTime(cell.row.original?.createdDate)}</Box>,
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "updatedDate",
      header: t("global.table.last_updated", "Last updated"),
      cell: ({ cell }) => <Box>{showDateTime(cell.row.original?.updatedDate)}</Box>,
      meta: { filter: DefaultColumnFilter }
    },
    {
      accessorKey: "actions",
      header: "",
      meta: {
        hiddenFilter: true,
        align: "right"
      },
      cell: ({ cell }) => {
        return (
          <StyledTableButtonGroup>
            {hasRole(appContext.appUser, allRoles.IssueReportsAdmin) && (
              <>
                <TableButton
                  type={TableButtonTypeEnum.Edit}
                  handleOnClick={() => {
                    history.push(allRoutes.IssuereportsArchiveIssuereport(cell.row.original.id))
                  }}
                />
                {/* {!appContext.appUser.customer.issueReportDeleteDisabled && (
                <TableButton
                  type={TableButtonTypeEnum.Delete}
                  handleOnClick={() => {
                    openContinueModal(cell.row.original.id)
                  }}
                />
              )} */}
              </>
            )}
          </StyledTableButtonGroup>
        )
      }
    }
  ]

  const subColumns = [
    {
      accessorKey: "id",
      header: t("global.table.id", "ID"),
      cell: ({ cell }) => {
        return <StyledBoldTypography>{cell.row.original.id}</StyledBoldTypography>
      }
    },
    {
      accessorKey: "regNumber",
      header: t("global.table.regnr", "Reg no"),
      cell: ({ cell }) => {
        return (
          <TableLink
            label={cell.row.original.regNumber}
            to={allRoutes.VehicleregistryVehiclesDetails(cell.row.original.id)}
          />
        )
      }
    },
    {
      accessorKey: "internNumber",
      header: t("global.table.internal_number", "Internal no")
    },
    {
      accessorKey: "categoryName",
      header: t("global.table.category", "Category"),
      filterFn: "arrIncludesSome",
      meta: {
        filter: MultiSelectFilter,
        mainFilter: true,
        filterHeader: t("global.table.category", "Category")
      }
    },
    {
      accessorKey: "description",
      header: t("global.table.description", "Description")
    },
    {
      accessorKey: "workOrderId",
      accessorFn: (row) => (row?.workOrderId > 0 ? `#${row.workOrderId}` : ""),
      header: t("global.table.workorder", "Work order")
    },
    {
      accessorFn: (row) => row.priority,
      header: t("global.table.priority", "Priority"),
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      meta: {
        filterOptions: [
          { value: 0, name: t("global.none", "None") },
          { value: 1, name: t("global.low", "Low") },
          { value: 2, name: t("global.medium", "Medium") },
          { value: 3, name: t("global.high", "High") }
        ],
        mainFilter: true,
        filter: MultiSelectFilter
      },
      cell: ({ cell }) => {
        return userHasIssueReportAdmin ? (
          <TableDropdown
            initialValue={cell.row.original.priority}
            handleOnChange={(event) => {
              setModalData({
                actionType: ActionType.Confirm,
                isOpen: true,
                title: t("global.change_priority", "Change priority"),
                message: `${t(
                  "global.change_the_priority_of",
                  "Are you sure you want to change the priority of"
                )} ${cell.row.original.name} ${t("global.from", "from")} ${prio(
                  cell.row.original.priority,
                  t
                )} ${t("global.to", "to")} ${prio(event.target.value, t)} ?`
              })
              setUpdatedIssueReportAttribute({ id: cell.row.original.id, priority: event.target.value })
            }}
            options={priorityOptions}
          />
        ) : (
          <ChipPriority type={cell.row.original.priority} />
        )
      }
    },
    {
      accessorFn: (row) => row.status,
      cell: ({ cell }) => {
        const currentStatusData = issueReportStatusData(cell.row?.original?.status, theme, t)
        return userHasIssueReportAdmin ? (
          <StatusDropdown
            onChange={(event) => {
              const newStatusData = issueReportStatusData(event.target.value, theme, t)
              setModalData({
                actionType: ActionType.Confirm,
                isOpen: true,
                title: t("global.change_status", "Change status"),
                message: `${t(
                  "global.change_the_status_of",
                  "Are you sure you want to change the status of"
                )} ${cell.row.original?.issueReportCategory?.name}
                ${t("global.from", "from")} ${currentStatusData.modalLabel} ${t("global.to", "to")} ${newStatusData.modalLabel
                  }?`
              })
              setUpdatedIssueReportAttribute({ id: cell.row.original.id, status: event.target.value })
            }}
            value={cell.row?.original?.status || 1}
            options={statusOptions}
            label={currentStatusData.label}
            icon={currentStatusData.icon}
            color={currentStatusData.color}
            backgroundColor={currentStatusData.backgroundColor}
            iconColor={currentStatusData.iconColor}
          />
        ) : (
          <ChipInfoIcon type={issueReportChip(cell.row?.original?.status)} />
        )
      },
      header: t("global.table.status", "Status"),
      filterFn: (row, columnId, filterValue) => includesPrimitiveFilter({ row, columnId, filterValue }),
      meta: {
        filter: MultiSelectFilter,
        filterHeader: t("global.table.status", "Status"),
        align: useMediaQuery(theme.breakpoints.up("xl")) ? "left" : "right",
        mainFilter: true,
        filterOptions:
          view === "issueReports"
            ? [
              {
                name: t("learning.titles.not_started", "Not Started"),
                value: 0
              },
              {
                name: t("global.new", "New"),
                value: 1
              },
              {
                name: t("global.planned", "Planned"),
                value: 2
              },
              {
                name: t("global.ongoing", "Ongoing"),
                value: 3
              },
              {
                name: t("global.fixed", "Fixed"),
                value: 4
              }
            ]
            : [{ name: t("global.completed", "Completed"), value: 5 }]
      }
    },
    {
      accessorKey: "updatedDate",
      header: t("global.table.last_updated", "Last updated"),
      cell: ({ cell }) => <Box>{showDateTime(cell.row.original?.updatedDate)}</Box>
    },
    {
      accessorKey: "actions",
      header: "",
      meta: {
        hiddenFilter: true,
        align: "right"
      },
      cell: ({ cell }) => {
        return (
          <StyledTableButtonGroup>
            {hasRole(appContext.appUser, allRoles.IssueReportsAdmin) && (
              <>
                <TableButton
                  type={TableButtonTypeEnum.Edit}
                  handleOnClick={() => {
                    history.push(allRoutes.IssuereportsArchiveIssuereport(cell.row.original.id))
                  }}
                />
                {/* {!appContext.appUser.customer.issueReportDeleteDisabled && (
                  <TableButton
                    type={TableButtonTypeEnum.Delete}
                    handleOnClick={() => {
                      openContinueModal(cell.row.original.id)
                    }}
                  />
                )} */}
              </>
            )}
          </StyledTableButtonGroup>
        )
      }
    }
  ]

  return (
    <div>
      <ModalDialog
        modalData={modalData}
        setModalData={setModalData}
        setShowSidebar={() => { }}
        handleSubmit={() => {
          issueReportPutApi.put(
            apiEndpoints.issuereportsadmin.updateissuereportattributes,
            updatedIssueReportAttribute
          )
        }}
        handleCancel={() => setUpdatedIssueReportAttribute({})}
      />

      {showContinueModal && (
        <ContinueDialog
          setShowContinueModal={setShowContinueModal}
          onAddSubmit={handleDelete}
          message={t("issuereports.delete_issuereport", "Do you want to delete the issue report?")}
          continueBtnText={t("issuereports.remove_issuereport", "Delete issue report")}
          cancelBtnText={t("global.cancel", "Cancel")}
        />
      )}

      {showSuccessModal && (
        <SuccessDialog
          resetModalState={setShowSuccessModal}
          title={t("issuereports.your_issuereports_is_deleted", "Your bug report has been deleted!")}
        />
      )}

      <ImportIssueReportForm updateExistingData={updateExistingData} />

      <Container>
        <Grid container direction="row" justifyContent="space-between" gap={2}>
          <Grid item>
            <Typography variant="h1">{t("issuereports.issuereports", "Issue reports")}</Typography>
          </Grid>

          <Grid item>
            <Grid container gap={2} justifyContent="space-between">
              {hasRole(appContext.appUser, allRoles.IssueReportsAdmin) && (
                <Grid item>
                  <Button
                    component={Link}
                    to={allRoutes.IssuereportsWorkordersAdd()}
                    variant="outlined"
                    color="primary">
                    {t("global.create_work_order", "Create work order")}
                  </Button>
                </Grid>
              )}
              <Grid item>
                <Button onClick={() => appContext.toggleSideBar(true)} variant="contained" color="primary">
                  {t("issuereports.create_issuereport", "Create issue report")}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Container>

      {issueReportsResults && issueReportsResults.status === 2 && (
        <Box paddingX={{ xs: 2, md: 3 }} pb={{ xs: 2, md: 3 }}>
          <Grid container direction="row" justifyContent="flex-end" alignItems="center" spacing={3}>
            <Grid item>
              <FormControlLabel
                sx={{ m: 0 }}
                label={t("global.group_by_vehcile", "Group by vehicle")}
                control={
                  <Switch
                    onChange={() => setGroupVehicles(!groupVehicles)}
                    id="groupVehicles"
                    checked={groupVehicles}
                  />
                }
              />
            </Grid>
            <Grid item>
              <ViewToggler view={view} setView={setView} viewList={viewList} disabled={groupVehicles} />
            </Grid>
          </Grid>
        </Box>
      )}
      <StyledTableContainer>
        {!groupVehicles ? (
          view === "issueReports" ? (
            <ClpTableServerSidePaging
              onRowClick={(row) => {
                history.push(allRoutes.IssuereportsArchiveIssuereport(row.original.id))
              }}
              hideFilter={false}
              columns={columns}
              tableId={ClpTableEnum.IssueReportsArchive}
              pagedDataApiEndpoint={apiEndpoints.issuereports.getpagedissuereportsarchive}
              nonPagedDataApiEndpoint={apiEndpoints.issuereports.getfilteredissuereportsarchive}
              refreshKey={refreshKey}
            />
          ) : (
            <ClpTable
              onRowClick={(row) => {
                history.push(allRoutes.IssuereportsArchiveIssuereport(row.original.id))
              }}
              rows={issueReportsResults.status === 2 && historyData}
              loadingState={issueReportsResults.status}
              hideFilter={false}
              columns={historyColumns}
              tableId={ClpTableEnum.IssueReportsArchiveHistory}

            />
          )
        ) : (
          <ClpTable
            rows={issueReportsGroupedResults.status === 2 && issueReportsGroupData}
            loadingState={issueReportsGroupedResults.status}
            subColumns={subColumns}
            columns={groupedColumns}
            refreshKey={refreshKey}

          />
        )}
      </StyledTableContainer>
    </div>
  )
}

export default Archive
